export const LANGUAGE_CHOICE_KEY = 'bb-language-choice';
export const LANGUAGE_AUTO_KEY = 'bb-language-auto-v2';

export function preferredLanguage(languages = [], available = [], saved) {
  const exact = raw => available.find(code => code.toLowerCase() === String(raw || '').toLowerCase());
  if (exact(saved)) return exact(saved);
  for (const raw of languages) {
    if (typeof raw !== 'string') continue;
    const value = raw.toLowerCase().replaceAll('_', '-');
    if (exact(value)) return exact(value);
    if (value === 'zh' || value.startsWith('zh-')) {
      const traditional = /(?:^|-)hant(?:-|$)|^zh-(?:tw|hk|mo)(?:-|$)/.test(value);
      const match = exact(traditional ? 'zh-TW' : 'zh-CN');
      if (match) return match;
      continue;
    }
    if (value === 'pt' || value.startsWith('pt-')) {
      if (exact('pt-BR')) return exact('pt-BR');
    }
    const base = value.split('-')[0];
    const match = exact(base) || available.find(code => code.toLowerCase().split('-')[0] === base);
    if (match) return match;
  }
  return exact('en') || available[0];
}

export function explicitLanguage(pathname, entries) {
  const path = String(pathname || '/').toLowerCase();
  return entries.find(entry => {
    const prefix = String(entry.root || '').replace(/\/$/, '').toLowerCase();
    return prefix && prefix !== '/' && (path === prefix || path.startsWith(prefix + '/'));
  })?.code;
}

export function languageReturnPath(pathname, search = '', hash = '', entries = []) {
  let path = pathname || '/';
  const entry = entries.find(item => explicitLanguage(path, [item]));
  if (entry) path = path.slice(entry.root.replace(/\/$/, '').length) || '/';
  return path + search + hash;
}

export function createLanguageController({ form, select, context, location, languages, read, write, sessionRead, sessionWrite, activity = () => ({}), pending = () => {}, status = () => {} }) {
  let queued;
  let submitting = false;
  const available = Array.from(select.options, option => option.value);
  const entries = context.available || [];
  const submit = (target, manual = false) => {
    if (!available.includes(target) || submitting) return false;
    if (manual) write(LANGUAGE_CHOICE_KEY, target);
    if (target === context.current) { queued = undefined; pending(false); return false; }
    const work = activity();
    if (work.pending > 0) { queued = { target, manual }; pending(true); status('waiting'); return false; }
    queued = undefined;
    pending(false);
    select.value = target;
    const input = form.querySelector('[name="return_to"]');
    if (input) input.value = work.returnTo ? languageReturnPath(work.returnTo, '', '', entries) : languageReturnPath(location.pathname, location.search, location.hash, entries);
    try { window.BBAnalytics?.track('language_change',{component:'language',language_to:target,source:manual?'manual':'automatic'}); } catch {}
    status('switching');
    submitting = true;
    form.setAttribute('aria-busy', 'true');
    try { form.submit(); } catch { submitting = false; form.removeAttribute('aria-busy'); status(''); return false; }
    return true;
  };
  return {
    change: target => submit(target, true),
    settled: () => queued ? submit(queued.target, queued.manual) : false,
    initialize() {
      if (context.editor || explicitLanguage(location.pathname, entries)) return { redirecting: false };
      const wanted = preferredLanguage(languages, available, read(LANGUAGE_CHOICE_KEY));
      if (!wanted || wanted === context.current || sessionRead(LANGUAGE_AUTO_KEY) === wanted) return { redirecting: false };
      sessionWrite(LANGUAGE_AUTO_KEY, wanted);
      return { redirecting: submit(wanted) };
    }
  };
}

if (typeof document !== 'undefined') {
  const form = document.getElementById('bb-language-form');
  const safeRead = (storage, key) => { try { return window[storage].getItem(key); } catch { return null; } };
  const safeWrite = (storage, key, value) => { try { window[storage].setItem(key, value); } catch {} };
  const finish = value => window.bbResolveLocale?.(value);
  if (!form) finish({ redirecting: false });
  else {
    const select = form.querySelector('select');
    const controller = createLanguageController({
      form, select, context: window.bbLocale || {}, location: window.location,
      languages: navigator.languages || [navigator.language],
      read: key => safeRead('localStorage', key), write: (key, value) => safeWrite('localStorage', key, value),
      sessionRead: key => safeRead('sessionStorage', key), sessionWrite: (key, value) => safeWrite('sessionStorage', key, value),
      activity: () => window.bbCartActivity || {},
      pending: value => { document.documentElement.dataset.bbLocalePending = String(value); },
      status: value => {
        const labels = window.bbText?.ui || {};
        form.querySelector('[data-bb-language-status]').textContent = value ? labels[value === 'waiting' ? 'language_waiting' : 'language_switching'] || '' : '';
      }
    });
    select.addEventListener('change', () => controller.change(select.value));
    form.addEventListener('submit', event => { event.preventDefault(); controller.change(select.value); });
    document.addEventListener('bb:cart-settled', () => controller.settled());
    try { finish(controller.initialize()); } catch { finish({ redirecting: false }); }
  }
}
